import { Controller } from "@hotwired/stimulus"
import { computePosition, offset, flip, shift, arrow, autoUpdate } from "@floating-ui/dom"

// usage
/*
  <div data-controller="makase-popover">
    <button data-makase-popover-target="trigger">Toggle Popover</button>
    <div data-makase-popover-target="content" class="hidden">
      This is a popover content.
    </div>
  </div>

  <!-- 고급 사용법 -->
  <div data-controller="makase-popover"
    data-makase-popover-trigger-value="hover"
    data-makase-popover-placement-value="top"
    data-makase-popover-alignment-value="center"
    data-makase-popover-offset-value="8"
    data-makase-popover-cross-offset-value="0"
    open
  >
    <button data-makase-popover-target="trigger">Hover me</button>
    <div data-makase-popover-target="anchor"></div> <!-- 선택적 anchor -->
    <div data-makase-popover-target="content" class="hidden">
      Content here
    </div>
  </div>

  <!-- 프리셋스타일사용 -->
  <div data-makase-popover-target="content" data-makase-popover-content-style class="hidden"></div>
*/

// 열려 있는 click 모드 팝오버 인스턴스를 추적
const openClickPopovers = new Set()

export default class MakasePopoverController extends Controller {
  // this.triggerTarget
  // this.triggerTargets
  // this.anchorTarget // 이것이 없으면 triggerTarget이 anchorTarget을 대체함
  // this.contentTarget
  static targets = [
    "trigger",
    "anchor",
    "content",
  ]

  // this.hasOpenClass : boolean // open 어트리뷰트가 있는지 여부
  // this.placementValue : top|right|bottom|left
  // this.alignmentValue : start|center|end
  // this.triggerValue : hover|click
  // this.offsetValue : number
  // this.crossOffsetValue : number
  static classes = ["open"]

  static values = {
    placement: { type: String, default: "bottom" },
    alignment: { type: String, default: "start" },
    trigger: { type: String, default: "click" },
    offset: { type: Number, default: 8 },
    crossOffset: { type: Number, default: 0 },
  }

  connect() {
    this.cleanup = null
    this.hoverTimeout = null
    this.isOpen = false

    // 이벤트 리스너 바인딩
    this.boundToggle = this.toggle.bind(this)
    this.boundShow = this.show.bind(this)
    this.boundHide = this.hide.bind(this)
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)

    // trigger 이벤트 설정
    this.setupTriggerEvents()

    // 초기 상태 설정 (open 어트리뷰트 확인)
    if (this.element.hasAttribute("open")) {
      this.show()
    } else {
      this.hide()
    }

    this.element.addEventListener("makase-popover:toggle", this.boundToggle)
    this.element.addEventListener("makase-popover:show", this.boundShow)
    this.element.addEventListener("makase-popover:hide", this.boundHide)

    // recall event
    this.element.addEventListener("makase-popover:recall", (event) => {
      let targetMatch = false
      if (typeof event.detail.target === "function") {
        targetMatch = event.detail.target(this.element)
      } else {
        targetMatch = (event.detail.target === this.element) || (this.element.id === event.detail?.id)
      }
      if (targetMatch) {
        event.stopPropagation()
        if (typeof event.detail.on === "function") return event.detail.on(this)
        console.warn("popover:recall event requires a controller or turboFrame function in its detail.")
      }
    })

  }

  disconnect() {
    this.removeTriggerEvents()
    this.stopPositionUpdates()
    openClickPopovers.delete(this)
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
    }
  }

  setupTriggerEvents() {
    if (!this.hasTriggerTarget) return

    if (this.triggerValue === "hover") {
      this.triggerTarget.addEventListener("mouseenter", this.boundShow)
      this.triggerTarget.addEventListener("mouseleave", this.boundHide)
      this.contentTarget.addEventListener("mouseenter", this.boundShow)
      this.contentTarget.addEventListener("mouseleave", this.boundHide)
    } else {
      // click (기본)
      this.triggerTarget.addEventListener("click", this.boundToggle)
    }
  }

  removeTriggerEvents() {
    if (!this.hasTriggerTarget) return

    if (this.triggerValue === "hover") {
      this.triggerTarget.removeEventListener("mouseenter", this.boundShow)
      this.triggerTarget.removeEventListener("mouseleave", this.boundHide)
      this.contentTarget.removeEventListener("mouseenter", this.boundShow)
      this.contentTarget.removeEventListener("mouseleave", this.boundHide)
    } else {
      this.triggerTarget.removeEventListener("click", this.boundToggle)
    }
  }

  toggle(event) {
    event?.stopPropagation?.()
    this.isOpen = !this.isOpen

    if (this.isOpen) {
      this.element.setAttribute("open", "")
      this.showContent()
    } else {
      this.element.removeAttribute("open")
      this.hideContent()
    }
  }

  show(event) {
    event?.stopPropagation?.()
    if (this.triggerValue === "hover" && this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      this.hoverTimeout = null
    }
    this.isOpen = true
    this.element.setAttribute("open", "")
    this.showContent()
  }

  hide(event) {
    event?.stopPropagation?.()
    if (this.triggerValue === "hover") {
      // hover 모드에서는 약간의 지연을 줌
      this.hoverTimeout = setTimeout(() => {
        this.isOpen = false
        this.element.removeAttribute("open")
        this.hideContent()
      }, 100)
    } else {
      this.isOpen = false
      this.element.removeAttribute("open")
      this.hideContent()
    }
  }

  showContent() {
    if (!this.hasContentTarget) return

    // 다른 열린 click 팝오버를 모두 닫음
    if (this.triggerValue === "click") {
      for (const other of openClickPopovers) {
        if (other !== this) other.hide()
      }
      openClickPopovers.add(this)
    }

    this.contentTarget.style.display = ""
    this.updatePosition()
    this.startPositionUpdates()

    // click 모드에서는 외부 클릭 감지
    if (this.triggerValue === "click") {
      setTimeout(() => {
        document.addEventListener("click", this.boundHandleClickOutside)
      }, 0)
    }
  }

  hideContent() {
    if (!this.hasContentTarget) return

    this.contentTarget.style.display = "none"
    this.stopPositionUpdates()

    if (this.triggerValue === "click") {
      openClickPopovers.delete(this)
      document.removeEventListener("click", this.boundHandleClickOutside)
    }
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hide()
    }
  }

  async updatePosition() {
    if (!this.hasContentTarget) return

    const anchorElement = this.hasAnchorTarget ? this.anchorTarget : this.triggerTarget
    if (!anchorElement) return

    // placement 생성 (alignment 포함)
    let placement = this.placementValue
    if (this.alignmentValue && this.alignmentValue !== "center") {
      placement = `${this.placementValue}-${this.alignmentValue}`
    }

    const { x, y } = await computePosition(anchorElement, this.contentTarget, {
      placement: placement,
      strategy: "fixed",
      middleware: [
        offset({
          mainAxis: this.offsetValue,
          crossAxis: this.crossOffsetValue,
        }),
        flip({
          fallbackPlacements: ['right', 'bottom', 'top'], // left가 안되면 right, bottom, top 순서로 시도
        }),
        shift({ padding: 5 }), // 화면 가장자리에서 약간 떨어뜨림
      ],
    })

    Object.assign(this.contentTarget.style, {
      position: "fixed",
      width: "max-content",
      left: `${x}px`,
      top: `${y}px`,
    })
  }

  startPositionUpdates() {
    if (!this.hasContentTarget) return

    const anchorElement = this.hasAnchorTarget ? this.anchorTarget : this.triggerTarget
    if (!anchorElement) return

    // autoUpdate로 스크롤/리사이즈 시 자동 위치 업데이트
    this.cleanup = autoUpdate(
      anchorElement,
      this.contentTarget,
      () => this.updatePosition()
    )
  }

  stopPositionUpdates() {
    if (this.cleanup) {
      this.cleanup()
      this.cleanup = null
    }
  }

  // 값 변경 감지
  placementValueChanged() {
    if (this.isOpen) this.updatePosition()
  }

  alignmentValueChanged() {
    if (this.isOpen) this.updatePosition()
  }

  offsetValueChanged() {
    if (this.isOpen) this.updatePosition()
  }

  crossOffsetValueChanged() {
    if (this.isOpen) this.updatePosition()
  }
}
